import * as React from "react";

import { FieldValues, useFormContext, useWatch } from "react-hook-form";

import { useValidators } from "../../../common/hooks/useValidators";

import { OutlinedTextFieldWysiwygProps } from "./type";

export const useOutlinedTextFieldWysiwyg = <T extends FieldValues>({
  defaultValue,
  error,
  name,
  rules,
  onChange,
  bindVariableClicks,
}: Pick<OutlinedTextFieldWysiwygProps<T>, "defaultValue" | "error" | "name" | "rules" | "onChange"> & {
  bindVariableClicks: () => void;
}) => {
  const { control, setValue: setFormValue } = useFormContext();
  const formValue = (useWatch({ control, name }) as string) ?? defaultValue;
  const [value, setValue] = React.useState<string | null>(formValue ?? null);

  React.useEffect(() => {
    if (formValue !== value) {
      setValue(formValue ?? null);
    }
  }, [formValue, value]);

  const validators = useValidators(rules);
  const errorMessage = error?.message;

  const isMounted = React.useRef(false);
  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleChange = React.useCallback(
    (newValue: unknown): void => {
      if (!isMounted.current || newValue === value) {
        return;
      }

      setValue(newValue as string);
      const isDirty = normalizeHtml(newValue as string) !== normalizeHtml(value as string);
      setFormValue(name, newValue as never, { shouldDirty: isDirty, shouldValidate: true });
      onChange?.(newValue as string);
      bindVariableClicks();
    },
    [value, setFormValue, name, onChange, bindVariableClicks]
  );

  return {
    error,
    handleChange,
    validators,
    errorMessage,
    value,
  };
};

export const normalizeHtml = (html: unknown) => {
  if (typeof html !== "string") {
    return;
  }

  const clean = html
    .replace(/&nbsp;/g, " ") // Replace non-breaking spaces with regular spaces
    .replace(/[\s\n\r]+/g, " ") // Replace all whitespace (including newlines) with a single space
    .replace(/ +/g, " ") // Replace multiple spaces with a single space
    .replace(/> </g, "><") // Remove space between tags
    .trim(); // Remove leading and trailing whitespace

  const div = document.createElement("div");
  div.innerHTML = clean;

  Array.from(div.getElementsByTagName("*")).forEach((element) => {
    const authoredStyle = element.getAttribute("fr-original-style") ?? element.getAttribute("style") ?? "";
    element.removeAttribute("class");
    element.removeAttribute("fr-original-class");
    element.removeAttribute("fr-original-style");
    element.setAttribute("style", authoredStyle);
    const canonicalStyle = (element as HTMLElement).style.cssText;
    if (canonicalStyle) {
      element.setAttribute("style", canonicalStyle);
    } else {
      element.removeAttribute("style");
    }
  });

  return div.innerHTML;
};
